/**
 * @file CartSizeGroup.tsx
 * @description Группа «Размер» «Вида корзины» (phase19.16, SB120 этап 4;
 * uiux-cart §4, db-design-sb120-stage4 §1.3 — пределы `SITE_THEME_LIMITS.cart`):
 *  - панель: ширина на телефоне 240–360 (↺ 300) и от 640 px 320–640 (↺ 448);
 *  - окно: ширина окна 320–480 (↺ 400) и строк до прокрутки 2–8 (↺ 4) —
 *    вместо полей панели; ширина «на телефоне» остаётся: окно на телефоне
 *    открывается панелью этой ширины.
 * Широкое и высокое — предупреждение под полем, не запрет.
 */

import { SITE_THEME_LIMITS } from "@chaiberi/shared";

import { SettingsGroup } from "../../ui/settings-groups";
import type { NumberPreset } from "../../site-fields/NumberPresets";
import { useCartLook } from "./cart-look-context";
import { CartFields } from "./CartFields";
import { CartNumberField } from "./CartNumberField";
import { groupHasWarning, sizeSummary } from "../../../lib/site-view/cart-look";

const L = SITE_THEME_LIMITS.cart;

const PHONE_PRESETS: readonly NumberPreset[] = [
  { label: "240", value: 240 },
  { label: "Как было 300", value: L.widthPhone.default },
  { label: "360", value: 360 },
];
const WIDE_PRESETS: readonly NumberPreset[] = [
  { label: "360", value: 360 },
  { label: "Как было 448", value: L.widthWide.default },
  { label: "520", value: 520 },
  { label: "640", value: 640 },
];
const POPOVER_PRESETS: readonly NumberPreset[] = [
  { label: "360", value: 360 },
  { label: "400", value: L.popoverWidth.default },
  { label: "480", value: 480 },
];
const ROWS_PRESETS: readonly NumberPreset[] = [
  { label: "3", value: 3 },
  { label: "4", value: L.popoverRows.default },
  { label: "6", value: 6 },
];

export function CartSizeGroup() {
  const { cart, warnings } = useCartLook();
  const { drawer, popover } = cart;
  const phone = (
    <CartNumberField
      label="Ширина на телефоне"
      path="cart.drawer.widthPhone"
      value={drawer.widthPhone}
      min={L.widthPhone.min}
      max={L.widthPhone.max}
      def={L.widthPhone.default}
      unit="px"
      presets={PHONE_PRESETS}
      hint={cart.mode === "popover" ? "На телефоне окно открывается панелью справа этой ширины." : "До 640 px экрана."}
      patch={(widthPhone) => ({ drawer: { ...drawer, widthPhone } })}
    />
  );
  return (
    <SettingsGroup id="size" title="Размер" hint="Ширина корзины на разных экранах." summary={sizeSummary(cart)} status={groupHasWarning(warnings, "size") ? "warning" : undefined}>
      <CartFields>
        {cart.mode === "drawer" ? (
          <>
            {phone}
            <CartNumberField
              label="Ширина от 640 px экрана"
              path="cart.drawer.widthWide"
              value={drawer.widthWide}
              min={L.widthWide.min}
              max={L.widthWide.max}
              def={L.widthWide.default}
              unit="px"
              largeStep={40}
              presets={WIDE_PRESETS}
              patch={(widthWide) => ({ drawer: { ...drawer, widthWide } })}
            />
          </>
        ) : (
          <>
            <CartNumberField
              label="Ширина окна"
              path="cart.popover.width"
              value={popover.width}
              min={L.popoverWidth.min}
              max={L.popoverWidth.max}
              def={L.popoverWidth.default}
              unit="px"
              presets={POPOVER_PRESETS}
              patch={(width) => ({ popover: { ...popover, width } })}
            />
            <CartNumberField
              label="Строк до прокрутки"
              path="cart.popover.rows"
              value={popover.rows}
              min={L.popoverRows.min}
              max={L.popoverRows.max}
              def={L.popoverRows.default}
              unit=""
              largeStep={2}
              presets={ROWS_PRESETS}
              hint="Дальше — прокрутка и строка «Ещё N товаров ниже»."
              patch={(rows) => ({ popover: { ...popover, rows } })}
            />
            {phone}
          </>
        )}
      </CartFields>
    </SettingsGroup>
  );
}
